import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { Spinner } from '../components/UI';
import { apiFetch } from '../services/api';
import {
  AppShell, TopNav, NavDrawer, BodyGrid, PageBody, useDrawer,
} from '../components/Layout';

const TYPE_META = {
  strategy: { icon: '📋', label: 'Strategy',    color: 'var(--ink)' },
  content:  { icon: '📝', label: 'Content',      color: '#5dcaa5'    },
  spec:     { icon: '📐', label: 'Product Spec', color: '#8b5cf6'    },
  research: { icon: '🔍', label: 'Research',     color: '#e8a87c'    },
  site:     { icon: '🌐', label: 'Website',      color: '#378add'    },
  campaign: { icon: '📣', label: 'Campaign',     color: '#c8563a'    },
};

const AGENT_COLORS = {
  ceo: '#0e0e0d', builder: '#378add', content: '#5dcaa5',
  marketing: '#c8563a', research: '#e8a87c', ops: '#c8563a', founder: '#0e0e0d',
};

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeWorkspace, loading: wsLoading } = useWorkspaces();
  const drawer = useDrawer();

  const [artifacts, setArtifacts]   = useState([]);
  const [artLoading, setArtLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterAgent, setFilterAgent] = useState(searchParams.get('agent') || 'all');

  useEffect(() => {
    setFilterAgent(searchParams.get('agent') || 'all');
  }, [searchParams]);

  const handleAgentFilter = (agent) => {
    setFilterAgent(agent);
    const next = new URLSearchParams(searchParams);
    if (agent === 'all') next.delete('agent'); else next.set('agent', agent);
    setSearchParams(next, { replace: true });
  };

  const workspaceId = searchParams.get('workspace_id') || activeWorkspace?.id;

  useEffect(() => {
    if (!workspaceId) return;
    setArtLoading(true);
    apiFetch(`/api/workspaces/${workspaceId}/artifacts`)
      .then(data => setArtifacts(Array.isArray(data) ? data : []))
      .catch(() => setArtifacts([]))
      .finally(() => setArtLoading(false));
  }, [workspaceId]);

  const filtered = artifacts.filter(a => {
    if (filterType  !== 'all' && a.type       !== filterType)  return false;
    if (filterAgent !== 'all' && a.agent_type !== filterAgent) return false;
    return true;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const typeOptions  = ['all', ...Object.keys(TYPE_META)];
  const agentOptions = ['all', 'ceo', 'builder', 'content', 'marketing', 'research', 'ops', 'founder'];

  return (
    <AppShell>
      <TopNav
        breadcrumb="Documents"
        drawerOpen={drawer.open}
        onToggleDrawer={drawer.toggle}
        drawerPinned={drawer.pinned}
      />
      <NavDrawer
        open={drawer.open}
        onClose={drawer.close}
        pinned={drawer.pinned}
        onTogglePin={drawer.togglePin}
      />

      <BodyGrid drawerPinned={drawer.pinned}>
        <PageBody>

          <div style={{ marginBottom: '16px' }}>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: '26px', fontWeight: '400', marginBottom: '4px' }}>
              Documents
            </h1>
            <p style={{ fontSize: '13px', color: 'var(--ink-mute)' }}>
              {filtered.length} document{filtered.length !== 1 ? 's' : ''}
              {(filterType !== 'all' || filterAgent !== 'all') ? ' (filtered)' : ''}
            </p>
          </div>

          {/* ── Horizontal filter bar ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
            marginBottom: '20px', padding: '12px 14px',
            background: 'var(--white)', border: '1px solid var(--paper-3)',
            borderRadius: 'var(--r)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={filterGroupLabel}>Type</span>
              {typeOptions.map(t => (
                <FilterChip key={t} active={filterType === t} onClick={() => setFilterType(t)}>
                  {t === 'all' ? 'All' : TYPE_META[t]?.label || t}
                  {t !== 'all' && (
                    <span style={{ marginLeft: '4px', opacity: 0.6 }}>
                      {artifacts.filter(a => a.type === t).length}
                    </span>
                  )}
                </FilterChip>
              ))}
            </div>

            <div style={{ width: '1px', height: '20px', background: 'var(--paper-3)', flexShrink: 0 }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={filterGroupLabel}>Agent</span>
              {agentOptions.map(a => (
                <FilterChip
                  key={a}
                  active={filterAgent === a}
                  onClick={() => handleAgentFilter(a)}
                  dot={a !== 'all' ? AGENT_COLORS[a] : null}
                >
                  {a === 'all' ? 'All' : a.charAt(0).toUpperCase() + a.slice(1)}
                </FilterChip>
              ))}
            </div>
          </div>

          {artLoading || wsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '48px' }}>
              <Spinner size={28} />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '48px 24px',
              border: '1px dashed var(--paper-3)', borderRadius: 'var(--r)',
            }}>
              <p style={{ fontSize: '14px', color: 'var(--ink-mute)' }}>
                {artifacts.length === 0
                  ? 'No documents yet — your agents will create them on the first run.'
                  : 'No documents match the current filters.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {filtered.map(artifact => (
      <ArtifactRow
        key={artifact.id}
        artifact={artifact}
        onClick={() => navigate(
          artifact.type === 'campaign' && artifact.agent_type === 'ad_campaign'
            ? `/dashboard/campaigns/${artifact.id}`
            : `/dashboard/artifacts/${artifact.id}`
        )}
      />
    ))}
            </div>
          )}
        </PageBody>
      </BodyGrid>
    </AppShell>
  );
}

function ArtifactRow({ artifact, onClick }) {
  const meta       = TYPE_META[artifact.type] || { icon: '📄', label: artifact.type, color: 'var(--ink-mute)' };
  const agentColor = AGENT_COLORS[artifact.agent_type] || '#888';
  const hasUrl     = !!artifact.live_url;

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--white)', border: '1px solid var(--paper-3)',
        borderRadius: 'var(--r)', padding: '14px 18px', cursor: 'pointer',
        display: 'grid', gridTemplateColumns: '32px 1fr auto',
        gap: '12px', alignItems: 'start', transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '6px',
        background: `${meta.color}14`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '15px', flexShrink: 0, marginTop: '1px',
      }}>
        {meta.icon}
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>{artifact.title}</span>
          <span style={{
            fontSize: '10px', fontWeight: '600', padding: '2px 7px', borderRadius: '100px',
            background: `${meta.color}18`, color: meta.color,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {meta.label}
          </span>
          {artifact.version > 1 && (
            <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>v{artifact.version}</span>
          )}
        </div>

        {hasUrl && (
          <p style={{ fontSize: '12px', color: 'var(--blue)', marginBottom: '6px', wordBreak: 'break-all' }}>
            {artifact.live_url}
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {artifact.agent_type && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '10px',
              background: `${agentColor}18`, color: agentColor,
            }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: agentColor }} />
              {artifact.agent_type}
            </span>
          )}
          <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
            {timeAgo(artifact.created_at)}
          </span>
        </div>
      </div>

      {artifact.is_latest && (
        <span style={{
          fontSize: '10px', fontWeight: '600', padding: '3px 8px', borderRadius: '10px',
          background: 'rgba(93,202,165,0.15)', color: '#2d8a6e',
          whiteSpace: 'nowrap', marginTop: '2px',
        }}>
          latest
        </span>
      )}
    </div>
  );
}

const filterGroupLabel = {
  fontSize: '11px', fontWeight: '600', letterSpacing: '0.06em',
  textTransform: 'uppercase', color: 'var(--ink-mute)', whiteSpace: 'nowrap', flexShrink: 0,
};

function FilterChip({ active, onClick, dot, children }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '4px 10px', borderRadius: '100px', fontSize: '12px',
      fontFamily: 'var(--sans)', cursor: 'pointer', whiteSpace: 'nowrap',
      border: `1px solid ${active ? 'var(--ink)' : 'var(--paper-3)'}`,
      background: active ? 'var(--ink)' : 'var(--white)',
      color: active ? 'var(--paper)' : 'var(--ink)',
      fontWeight: active ? '500' : '400', transition: 'all 0.12s',
    }}>
      {dot && (
        <span style={{
          width: '6px', height: '6px', borderRadius: '50%',
          background: active ? 'var(--paper)' : dot, flexShrink: 0,
        }} />
      )}
      {children}
    </button>
  );
}